import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchBoards, 
  fetchPosts, 
  fetchPostDetail, 
  createPost, 
  updatePost, 
  deletePost, 
  createComment,
  selectBoards,
  selectBoardsLoading, 
  selectPosts, 
  selectPostsLoading, 
  selectPostDetail, 
  selectPostDetailLoading,
  selectPostsPagination,
  selectUser
} from '../redux/WithcookieSlice';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button, Box, Typography } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ListIcon from '@mui/icons-material/List';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from '../redux/TranslationHelper';

function BoardComponent({ style, data = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation(); // 현재 위치 정보 가져오기
  const { target } = useParams(); // URL 파라미터 추출
  const { t } = useTranslation();
  
  // 게시판, 게시글, 모드 관련 상태 관리
  const [mode, setMode] = useState('list'); // 'list', 'detail', 'new', 'edit'
  const [boardCode, setBoardCode] = useState('');
  const [postId, setPostId] = useState(null);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '' });
  const [commentData, setCommentData] = useState({ content: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // 리덕스 상태 가져오기
  const boards = useSelector(selectBoards);
  const boardsLoading = useSelector(selectBoardsLoading);
  const posts = useSelector(selectPosts);
  const postsLoading = useSelector(selectPostsLoading);
  const postDetail = useSelector(selectPostDetail);
  const postDetailLoading = useSelector(selectPostDetailLoading);
  const pagination = useSelector(selectPostsPagination);
  
  // 현재 로그인한 사용자 정보 가져오기
  const currentUser = useSelector(selectUser);
  
  // 현재 디바이스 감지 (모바일 또는 PC)
  const [currentDevice, setCurrentDevice] = useState(() => {
    // 초기값을 함수로 제공하여 컴포넌트 마운트 전에 한 번만 실행
    // 페이지 로드 시 즉시 현재 화면 크기에 맞는 디바이스 타입 감지
    return window.innerWidth <= 768 ? 'mobile' : 'pc';
  });
  
  // 강제 업데이트 상태값
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // 초기 렌더링 플래그 및 타이머 관리
  const initialRenderRef = useRef(true);
  const timerRef = useRef(null);
  const styleProcessedRef = useRef(false);
  const mountTimeRef = useRef(Date.now());
  const loadedRef = useRef(false); // 중복 로드 방지용 플래그 추가
  
  // 글로벌 이벤트 트리거 (문서 전체가 로드된 후 스타일 재적용)
  useEffect(() => {
    const applyStylesOnFullLoad = () => {
      setForceUpdate(prev => prev + 1);
    };
    
    window.addEventListener('load', applyStylesOnFullLoad);
    return () => window.removeEventListener('load', applyStylesOnFullLoad);
  }, []);
  
  // 화면 크기에 따라 디바이스 타입 결정 (최우선 실행)
  useLayoutEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      const newDevice = isMobile ? 'mobile' : 'pc';
      
      if (newDevice !== currentDevice) {
        setCurrentDevice(newDevice);
        // 디바이스 타입이 변경된 경우에만 forceUpdate 증가
        setForceUpdate(prev => prev + 1);
      }
    };
    
    // 초기 실행
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentDevice]);
  
  // 스타일 초기화 및 재적용 (렌더링 전에 호출) - 초기 렌더링 시 한 번만 실행되도록 수정
  useLayoutEffect(() => {
    // PC 또는 모바일 모드일 때 특별한 초기화 로직 추가
    if (initialRenderRef.current && !styleProcessedRef.current) {

      
      
      // 단일 타이머 설정 - 더 짧은 시간으로 조정
      timerRef.current = setTimeout(() => {
        setForceUpdate(prev => prev + 1);
        // 약간의 지연 후 한 번 더 강제 업데이트 (배포 환경에서 확실히 적용되도록)
        setTimeout(() => {
          setForceUpdate(prev => prev + 1);
          styleProcessedRef.current = true;
        }, 100);
      }, 50);
    }
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [style]);

  // 스타일 변경 감지 및 적용 - 중복 업데이트 방지
  useLayoutEffect(() => {
    // 이전 타이머 정리
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // 배포 환경에서도 스타일 적용을 위해 항상 최소 1회 업데이트
    setForceUpdate(prev => prev + 1);
    
    timerRef.current = setTimeout(() => {
      // 배포 환경에서 확실히 적용되도록 한 번 더 업데이트
      setForceUpdate(prev => prev + 1);
      styleProcessedRef.current = true;
    }, 50);
  }, [style, currentDevice]);
  
  // 현재 디바이스에 맞는 설정값 가져오기
  const getDeviceValue = (field, defaultValue) => {
    // 기본 검증
    if (!data || Array.isArray(data)) {
      return defaultValue;
    }
    
    try {
      // 1. 현재 디바이스 설정 확인 (최우선)
      if (data.device && 
          data.device[currentDevice] && 
          data.device[currentDevice][field] !== undefined) {
        
        const value = data.device[currentDevice][field];
        return value;
      }
      
      // 2. 공통 설정 확인
      if (data[field] !== undefined) {
        const value = data[field];
        return value;
      }
      
      // 3. 기본값 반환
      return defaultValue;
    } catch (error) {
      console.error(`[BoardComponent] 데이터 처리 오류:`, error);
      return defaultValue;
    }
  };
  
  // 데이터 구조 처리
  let boardData = [];
  let boardTitle = getDeviceValue('title', '게시판');
  let boardParameter = getDeviceValue('parameter', '');
  
  // 배열일 경우와 객체일 경우를 모두 처리
  if (Array.isArray(data)) {
    boardData = data;
  } else if (data && typeof data === 'object') {
    boardData = data.items || [];
  }
  
  // 스타일 적용 함수 단순화
  const getSimpleStyle = (styleKey, defaultValue) => {
    return style?.[styleKey] || defaultValue;
  };

  // 스타일 계산을 강제 업데이트에 의존하도록 변경
  const computedStyle = React.useMemo(() => {
    // 모든 패딩 스타일 미리 계산 - 기본값 '0px'로 변경
    const paddings = {
      paddingTop: getSimpleStyle('paddingTop', '0px'),
      paddingRight: getSimpleStyle('paddingRight', '0px'),
      paddingBottom: getSimpleStyle('paddingBottom', '0px'),
      paddingLeft: getSimpleStyle('paddingLeft', '0px')
    };
    
    // 추가 로깅으로 디버깅
    
    
    const borderRadius = getSimpleStyle('borderRadius', '0px');
    
    // 스타일 객체 구성 - 패딩 값을 확실히 적용
    return {
      fontFamily: 'Inter, sans-serif',
      width: '100%',
      height: '100%',
      overflow: 'auto',
      paddingTop: paddings.paddingTop,
      paddingRight: paddings.paddingRight,
      paddingBottom: paddings.paddingBottom,
      paddingLeft: paddings.paddingLeft,
      backgroundColor: getSimpleStyle('backgroundColor', 'transparent'),
      borderRadius: borderRadius,
      boxShadow: getSimpleStyle('boxShadow', 'none'),
      // 다른 스타일 속성 유지
      ...(Object.keys(style || {}).filter(key => 
        key !== 'device' && 
        !['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 
          'backgroundColor', 'borderRadius', 'boxShadow'].includes(key)
      ).reduce((acc, key) => {
        acc[key] = style[key];
        return acc;
      }, {}))
    };
  }, [style, currentDevice, forceUpdate]);
  
  // 게시판 파라미터 숫자로 변환 (API에서 category_id로 사용)
  let categoryId = null;
  if (boardParameter) {
    // 1. 순수한 숫자인 경우 바로 변환
    if (!isNaN(boardParameter)) {
      categoryId = parseInt(boardParameter, 10);
    } 
    // 2. 문자열 내에 숫자가 있는 경우 (예: 'board1')
    else {
      // 숫자만 추출하는 정규 표현식 사용
      const matches = boardParameter.match(/\d+/);
      if (matches && matches.length > 0) {
        categoryId = parseInt(matches[0], 10);
      }
    }
  }
  
 

  // 컴포넌트 초기화 시 게시판 목록 로드 - 최초 1회만 실행되도록 수정
  useEffect(() => {
    // 이미 로드되었다면 다시 로드하지 않음
    if (loadedRef.current) return;
    
    // 초기화 로그는 여기서만 출력
  
      
    // 최초 1회만 게시판 목록 로드
    dispatch(fetchBoards());
    
    // parameter 값이 있으면 해당 카테고리 ID로 게시글 목록 불러오기
    if (categoryId) {
      dispatch(fetchPosts({
        categoryId: categoryId,
        page: 1,
        perPage
      }));
      setCurrentPage(1);
      
      // 파라미터로 받은 카테고리 ID를 선택된 게시판 ID로 설정
      setSelectedBoardId(categoryId);
    }
    
    // URL 파싱은 parameter가 없을 때만 수행
    else if (!categoryId) {
      // 새로고침 대응: URL에서 즉시 게시글 ID 추출 및 요청
      const path = location.pathname;
      const pathSegments = path.split('/').filter(Boolean);
      
      // URL 형식이 /boardCode/postId인 경우
      if (pathSegments.length === 2 && !isNaN(pathSegments[1])) {
        const postIdFromUrl = pathSegments[1];
        const boardCodeFromUrl = pathSegments[0];
        
        setMode('detail');
        setPostId(postIdFromUrl);
        setBoardCode(boardCodeFromUrl.toUpperCase());
        
        // 게시글 상세 정보 요청 (게시판 목록 로드와 독립적으로)
        dispatch(fetchPostDetail(postIdFromUrl));
      }
    }
    
    // 초기 렌더링 완료 표시
    initialRenderRef.current = false;
    loadedRef.current = true;  // 로드 완료 표시
  }, [dispatch, location.pathname, categoryId, perPage, boardParameter]);

  // URL 파싱용 ref 추가
  const urlParsingRef = useRef(false);
  
  // 브라우저 뒤로가기/앞으로가기 감지 및 처리
  useEffect(() => {
    const handlePopState = () => {
      
      // 게시글 목록으로 돌아가는 경우와 상세 페이지로 가는 경우를 구분
      const segments = location.pathname.split('/').filter(Boolean);
      
      // 페이지를 다시 로드하여 상태를 갱신
      window.location.reload();
    };
    
    // popstate 이벤트 리스너 등록
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location.pathname]);

  // URL 경로를 분석하여 필요한 정보를 추출하는 useEffect - parameter 값과 관계없이 항상 실행
  useEffect(() => {
    // 이미 파싱 중이면 중복 실행 방지
    if (urlParsingRef.current) {
      return;
    }
    
    // 아직 초기화가 완료되지 않았으면 실행하지 않음
    if (initialRenderRef.current) {
      return;
    }
    
    // 파싱 시작 표시
    urlParsingRef.current = true;
    
    try {
      // parameter 값이 있어도 URL 파싱은 항상 수행 (라우팅과 parameter는 별개)
      
      // 루트 경로인 경우 무시
      if (!location.pathname || location.pathname === '/') {
        urlParsingRef.current = false;
        return;
      }
      
      // 경로 세그먼트 추출 (첫 번째 '/'를 제거)
      const segments = location.pathname.split('/').filter(segment => segment);
      
      // 세그먼트가 존재하지 않으면 무시
      if (!segments || segments.length === 0) {
        urlParsingRef.current = false;
        return;
      }
      
      // 첫 번째 세그먼트는 게시판 코드
      const extractedBoardCode = segments[0].toUpperCase();
      
      // 게시판 코드 설정 (목록 의존성 없이)
      setBoardCode(extractedBoardCode);
      
      // 두 번째 세그먼트 분석
      if (segments.length === 1) {
        // URL 패턴: /:target
        setMode('list');
        setPostId(null);
        setFormData({ title: '', content: '' });
        
        // 게시판 목록이 로드되었고 URL로 찾은 게시판과 parameter로 지정된 게시판이 다른 경우에만
        // selectedBoardId 설정과 게시글 로드를 실행
        if (boards && boards.length > 0) {
          const foundBoard = boards.find(board => board.code === extractedBoardCode);
          
          if (foundBoard && (!selectedBoardId || foundBoard.id !== selectedBoardId)) {
            setSelectedBoardId(foundBoard.id);
            
            // 게시글 목록 요청 시 categoryId(parameter)를 우선 사용하고,
            // parameter가 없는 경우에만 URL에서 추출한 ID를 사용
            dispatch(fetchPosts({
              categoryId: categoryId || foundBoard.id,
              page: 1,
              perPage
            }));
            setCurrentPage(1);
          }
        }
      } else if (segments.length === 2) {
        if (segments[1] === 'new') {
          // URL 패턴: /:target/new
          setMode('new');
          setPostId(null);
          setFormData({ title: '', content: '' });
        } else if (!isNaN(segments[1])) {
          // URL 패턴: /:target/:postId
          const viewPostId = segments[1];
          setMode('detail');
          setPostId(viewPostId);
          
          // 게시글 상세 정보 요청 (게시판 목록 로드와 독립적으로)
          dispatch(fetchPostDetail(viewPostId));
        } else {
          console.error(`[URL 파싱] 두 번째 세그먼트 ${segments[1]}가 숫자나 'new'가 아니므로 처리할 수 없습니다.`);
        }
      } else {
        // 알 수 없는 URL 패턴
        console.warn('[URL 파싱] 알 수 없는 URL 패턴입니다:', location.pathname);
      }
    } catch (error) {
      console.error('[URL 파싱] URL 처리 중 오류가 발생했습니다:', error);
    } finally {
      // 파싱 완료 표시 (다음 URL 변경 시 다시 파싱할 수 있도록)
      setTimeout(() => {
        urlParsingRef.current = false;
      }, 100);
    }
  }, [location.pathname, dispatch, perPage, boards, categoryId]); // selectedBoardId 제거

  // 게시판 ID 설정 - 의존성 배열 최소화하여 중복 실행 방지
  useEffect(() => {
    // 이미 선택된 ID가 있거나 boards가 없으면 건너뜀
    if (selectedBoardId || !boards || boards.length === 0) {
      return;
    }
    
    // parameter 값이 있으면 그대로 사용
    if (categoryId) {
      // 이미 초기 useEffect에서 설정했다면 중복 설정 방지
      if (!selectedBoardId) {
        setSelectedBoardId(categoryId);
        // boards가 있으면 boardCode도 설정
        const foundBoard = boards.find(board => board.id === categoryId);
        if (foundBoard) {
          setBoardCode(foundBoard.code);
        }
      }
      return;
    }
    
    // URL에서 게시판 코드 추출 - 최초 1회만 실행
    if (boardCode && !selectedBoardId) {
      const foundBoard = boards.find(board => board.code === boardCode);
      
      if (foundBoard) {
        setSelectedBoardId(foundBoard.id);
      } else if (boards.length > 0) {
        // URL로 게시판을 찾지 못했을 때 첫 번째 게시판 사용
        setSelectedBoardId(boards[0].id);
        setBoardCode(boards[0].code);
      }
    }
  }, [boards, categoryId, selectedBoardId, boardCode]);

  // 모드 변경 시 효과 추적
  useEffect(() => {
    
    
    // new 모드일 때 게시판 ID 설정 (renderNewMode에서 수행하던 작업을 여기로 이동)
    if (mode === 'new') {
      // URL 경로와 target 파라미터 확인
      const urlPathSegments = location.pathname.split('/').filter(Boolean);
      const urlPath = urlPathSegments.length > 0 ? urlPathSegments[0] : '';
      
      // parameter 속성이 있으면 우선 사용
      if (categoryId && !selectedBoardId) {
        setSelectedBoardId(categoryId);
      } 
      // URL 기반 처리
      else if (!selectedBoardId && boards && boards.length > 0) {
        const routeTarget = urlPath || target || boardParameter;
        
        if (routeTarget) {
          const foundBoard = boards.find(board => 
            board.code === routeTarget.toUpperCase() || 
            board.code === String(routeTarget).toUpperCase()
          );
          
          if (foundBoard) {
            setSelectedBoardId(foundBoard.id);
          }
        }
      }
    }
  }, [mode, location.pathname, categoryId, selectedBoardId, boards, target, boardParameter]);

  // currentPage 변경 시 게시글 다시 로드 (페이징 처리)
  useEffect(() => {
    // 초기 렌더링이 아닐 때만 페이지 변경 시 게시글 목록 다시 로드
    if (!initialRenderRef.current && mode === 'list') {
      
      // 현재 선택된 게시판이 있는 경우에만 API 호출
      if (categoryId || selectedBoardId) {
        const boardId = categoryId || selectedBoardId;
        dispatch(fetchPosts({
          categoryId: boardId,
          page: currentPage,
          perPage
        }));
      }
    }
  }, [currentPage, dispatch, categoryId, selectedBoardId, perPage, mode]);

  // 폼 데이터 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 댓글 입력 변경 핸들러
  const handleCommentChange = (e) => {
    setCommentData(prev => ({ ...prev, content: e.target.value }));
  };

  // 게시글 저장 핸들러
  const handleSavePost = () => {
    if (!formData.title.trim()) {
      alert(t('please_enter_title'));
      return;
    }

    if (!formData.content.trim()) {
      alert(t('please_enter_content'));
      return;
    }
    
    if (mode === 'new') {
      // 백엔드 API 요청 시 항상 categoryId(parameter)를 우선 사용
      // parameter가 없는 경우에만 URL에서 추출한 ID 사용
      const boardId = categoryId || selectedBoardId;
      
      if (!boardId) {
        alert(t('please_select_board'));
        return;
      }
      
      const postData = {
        category_id: boardId, // API 요청 형식에 맞게 category_id로 설정
        title: formData.title,
        content: formData.content,
        edit_type: 'WYSIWYG'
      };
      
      
      dispatch(createPost(postData))
        .unwrap()
        .then(response => {
          
          
          // 게시글 ID 추출 (응답 구조에 따라 조정 필요)
          const newPostId = response.post ? response.post.id : response.id;
          
          if (newPostId) {
            // 성공 메시지 표시 후 상세 페이지로 이동
            alert(t('post_created_success'));
            navigateToDetail(newPostId);
          } else {
            // 응답에서 ID를 찾을 수 없는 경우 목록으로 이동
            alert(t('post_created_but_no_detail'));
            navigateToList();
          }
        })
        .catch(error => {
          console.error('[게시글 생성] 실패:', error);
          alert(t('post_create_failed') + ': ' + (error.message || t('unknown_error')));
        });
    } else if (mode === 'edit' && postDetail) {
      // API 응답 구조에 따라 게시글 ID 추출
      const post = postDetail.post || postDetail;
      const postId = post.id;
      
      const postData = {
        postId: postId,
        postData: {
          title: formData.title,
          content: formData.content
        }
      };
      
      
      dispatch(updatePost(postData))
        .unwrap()
        .then(response => {
          alert(t('post_updated_success'));
          navigateToDetail(postId);
          setMode('detail');
        })
        .catch(error => {
          console.error('[게시글 수정] 실패:', error);
          alert(t('post_update_failed') + ': ' + (error.message || t('unknown_error')));
        });
    }
  };

  // 댓글 저장 핸들러
  const handleSaveComment = () => {
    if (!commentData.content.trim()) {
      alert(t('please_enter_comment'));
      return;
    }

    if (postDetail) {
      // API 응답 구조에 따라 게시글 ID 추출
      const post = postDetail.post || postDetail;
      const postId = post.id;

      dispatch(createComment({
        postId: postId,
        commentData: {
          content: commentData.content,
          edit_type: 'WYSIWYG'
        }
      })).then(() => {
        setCommentData({ content: '' });
        
        // 댓글 추가 후 게시글 상세 정보 다시 불러오기
        dispatch(fetchPostDetail(postId));
      });
    }
  };

  // 게시글 삭제 핸들러
  const handleDeletePost = () => {
    if (postDetail) {
      // API 응답 구조에 따라 게시글 ID 추출
      const post = postDetail.post || postDetail;
      const postId = post.id;
      
      dispatch(deletePost(postId))
        .unwrap()
        .then(() => {
          setShowDeleteModal(false);
          
          // 삭제 성공 메시지 표시
          alert(t('post_deleted_success'));
          
          // 명시적으로 게시판 목록 페이지로 이동
          // URL에서 게시판 코드 추출 - 원래 경로 그대로 유지
          const pathSegments = location.pathname.split('/').filter(Boolean);
          const boardCodeFromPath = pathSegments.length > 0 ? pathSegments[0] : '';
          
          if (boardCodeFromPath) {
            navigate(`/${boardCodeFromPath}/:target`);
          } 
          
          // 모드 변경
          setMode('list');
        })
        .catch(error => {
          console.error('[게시글 삭제] 실패:', error);
          alert(t('post_delete_failed') + ': ' + (error.message || t('unknown_error')));
        });
    }
  };

  // 페이지 이동 핸들러
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    
    // 페이지 변경 시 게시글 목록 다시 불러오기
    if (categoryId) {
      // parameter 속성 기반으로 게시글 목록 로드
      dispatch(fetchPosts({
        categoryId: categoryId,
        page: newPage,
        perPage
      }));
    } else if (selectedBoardId) {
      // 선택된 게시판 ID 기반으로 게시글 목록 로드
      dispatch(fetchPosts({
        categoryId: selectedBoardId,
        page: newPage,
        perPage
      }));
    } else if (boards && boards.length > 0 && boardCode) {
      // 게시판 코드로 ID 찾아서 게시글 목록 로드
      const foundBoard = boards.find(board => board.code === boardCode);
      if (foundBoard) {
        dispatch(fetchPosts({
          categoryId: foundBoard.id,
          page: newPage,
          perPage
        }));
      }
    }
  };
  
  // 현재 라우팅 경로 가져오기 (URL의 target 또는 props의 boardParameter 사용)
  const getCurrentPath = () => {
    // parameter가 있으면 우선 사용
    if (boardParameter) {
      return boardParameter;
    }
    
    // URL 경로에서 첫 번째 세그먼트 가져오기 (게시판 코드)
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const boardCode = pathSegments.length > 0 ? pathSegments[0] : '';
    
    // 경로가 있으면 경로 반환, 없으면 URL 파라미터 또는 boardParameter 사용
    return boardCode || target || '';
  };

  // 게시글 목록 페이지로 이동하는 함수
  const navigateToList = (boardCodeParam) => {
    
    // 모드 직접 변경
    setMode('list');
    setPostId(null);
    setFormData({ title: '', content: '' });
    
    try {
      // boardCodeParam이 객체인지 확인하고 적절히 처리
      let boardCodeValue;
      
      if (typeof boardCodeParam === 'object' && boardCodeParam !== null) {
        // 객체에서 code 또는 id 필드 추출
        if (boardCodeParam.code) {
          boardCodeValue = boardCodeParam.code;
        } else if (boardCodeParam.id) {
          // ID로 게시판 찾기
          const boardWithId = boards.find(b => b.id === boardCodeParam.id);
          boardCodeValue = boardWithId ? boardWithId.code : '';
        } else {
          // 객체지만 code나 id가 없는 경우 현재 경로에서 추출하거나 기존 boardCode 사용
          const pathSegments = location.pathname.split('/').filter(Boolean);
          boardCodeValue = pathSegments.length > 0 ? pathSegments[0] : (boardCode || '');
        }
      } else {
        // 문자열이나 다른 타입이면 그대로 사용
        boardCodeValue = boardCodeParam || boardCode || '';
      }
      
      // 명시적으로 문자열로 변환 (이미 문자열이 아닌 경우에만)
      const targetBoardCode = boardCodeValue.toString();
      
      
      // 빈 문자열이면 현재 경로 세그먼트 사용
      if (!targetBoardCode) {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        if (pathSegments.length > 0) {
          const currentBoardCode = pathSegments[0]+ pathSegments[1];
          const targetPath = `/${currentBoardCode}/:target`;
          navigate(targetPath);
        } else {
          // 경로도 없으면 루트로 이동
          navigate('/');
        }
      } else {
        // 목록 경로 - 사용자 입력 그대로 사용
        const targetPath = `/${targetBoardCode}/:target`;
        navigate(targetPath);
      }
      
      // parameter 속성이 있으면 해당 게시판 데이터 로드, 없으면 URL 기반으로 로드
      if (categoryId) {
        dispatch(fetchPosts({
          categoryId: categoryId,
          page: 1,
          perPage
        }));
      } else if (boards && boards.length > 0) {
        // 대소문자 구분하지 않고 코드 찾기
        const foundBoard = boards.find(board => board.code.toUpperCase() === targetBoardCode.toUpperCase());
        if (foundBoard) {
          dispatch(fetchPosts({
            categoryId: foundBoard.id,
            page: 1,
            perPage
          }));
        }
      }
      
      setCurrentPage(1);
      
    } catch (error) {
      console.error('[네비게이션] 목록 화면 이동 중 오류 발생:', error);
      
      // 오류 발생 시 기본 경로로 이동 시도
      navigate('/');
    }
  };

  // 새 글 작성 버튼
  const NewPostButton = () => {
    // 컴포넌트 함수 내부에서의 로그는 제거하고 필요시 useEffect로 이동
    
    // 게시판 코드가 객체인지 확인하고 객체에서 code나 id를 추출
    const handleClick = () => {
      // 현재 URL 경로에서 게시판 코드 추출 - 사용자가 입력한 코드 그대로 사용
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const pathBoardCode = pathSegments.length > 0 ? pathSegments[0] : '';
      
      // URL에서 추출한 경로 그대로 사용
      if (pathBoardCode) {
        navigateToNew(pathBoardCode);
      } else if (boardCode) {
        navigateToNew(boardCode);
      } else if (categoryId && boards && boards.length > 0) {
        // 게시판 코드를 찾아 navigateToNew 호출
        const foundBoard = boards.find(board => board.id === categoryId);
        if (foundBoard) {
          navigateToNew(foundBoard.code);
        } else {
          // 기본값을 지정하지 않고 빈 문자열 전달
          navigateToNew('');
        }
      } else {
        // 기본값을 지정하지 않고 빈 문자열 전달
        navigateToNew('');
      }
    };
    
    return (
      <Button  
        variant="contained" 
        onClick={handleClick}
        startIcon={<AddIcon />}
        color="primary"
        sx={{ ml: 'auto' }}
        style={{margin: '10px 20px' }}
      >
        새 글 작성
      </Button>
    );
  };

  

  // 새 게시글 작성 페이지로 이동하는 함수
  const navigateToNew = (boardCodeParam) => {
    
    // 모드 직접 변경 (navigate 전에 모드부터 변경)
    setMode('new');
    setPostId(null);
    setFormData({ title: '', content: '' });
    
    try {
      // boardCodeParam 그대로 사용
      let boardCodeValue = boardCodeParam || boardCode;
      
      // 문자열로 변환만 하고 대소문자 변환 없음
      const targetBoardCode = String(boardCodeValue);
      
      
      // URL 생성 및 이동 - 사용자 입력 경로 유지, 대소문자 변환 없음
      const targetPath = `/${targetBoardCode}/new`;
      
      // navigate 직접 호출 (replace: false로 설정하여 히스토리에 추가)
      navigate(targetPath, { replace: false });
      
    } catch (error) {
      console.error('[네비게이션] 새 게시글 작성 화면 이동 중 오류 발생:', error);
    }
  };

  // 게시글 상세 페이지로 이동하는 함수
  const navigateToDetail = (postIdParam, boardCodeParam) => {

    try {
      if (!postIdParam) {
        console.error('[네비게이션] 게시글 ID가 없습니다. 이동이 취소됩니다.');
        return;
      }
      
      // 모드 직접 변경
      setMode('detail');
      setPostId(postIdParam);
      
      // 게시글 상세 정보와 댓글 함께 불러오기 - 객체 대신 직접 ID 전달
      dispatch(fetchPostDetail(postIdParam));
      
      // boardCodeParam이 객체인지 확인
      let boardCodeValue;
      
      if (typeof boardCodeParam === 'object' && boardCodeParam !== null) {
        // 객체에서 code 또는 id 필드 추출
        if (boardCodeParam.code) {
          boardCodeValue = boardCodeParam.code;
        } else if (boardCodeParam.id) {
          // ID로 게시판 찾기
          const boardWithId = boards.find(b => b.id === boardCodeParam.id);
          boardCodeValue = boardWithId ? boardWithId.code : '';
        } else {
          boardCodeValue = '';
        }
      } else {
        // 문자열이나 다른 타입이면 그대로 사용
        boardCodeValue = boardCodeParam || '';
      }
      
      // 현재 경로에서 마지막 세그먼트 추출하고 게시글 ID로 대체
      const pathSegments = location.pathname.split('/').filter(Boolean);
      let targetPath;
      
      if (pathSegments.length >= 1) {
        // 원래 경로 그대로 사용 (대소문자 유지)
        const boardCodeFromPath = pathSegments[0];
        targetPath = `/${boardCodeFromPath}/${postIdParam}`;
      } else if (boardCodeValue) {
        // boardCodeParam에서 추출한 값 사용
        targetPath = `/${boardCodeValue}/${postIdParam}`;
      } else if (boardCode) {
        // 경로가 비어있거나 없는 경우 boardCode 사용
        targetPath = `/${boardCode}/${postIdParam}`;
      } else {
        // 모든 값이 없는 경우 오류 로그
        console.error('[네비게이션] 게시판 코드가 없어 이동할 수 없습니다.');
        return;
      }
      
      navigate(targetPath, { replace: false });
      
    } catch (error) {
      console.error('[네비게이션] 게시글 상세 화면 이동 중 오류 발생:', error);
    }
  };

  // 게시글 수정 모드로 전환 핸들러
  const handleGoToEdit = () => {
    // API 응답 구조에 따라 게시글 데이터 추출
    const post = postDetail.post || postDetail;
    
    setFormData({
      title: post.title,
      content: post.content
    });
    setMode('edit');
  };

  // 현재 게시판 이름 가져오는 함수
  const getBoardTitle = () => {
    // 기본값
    let title = getDeviceValue('title', '게시판');
    
    // 게시판 목록이 있고, 게시판 코드가 있는 경우 해당 게시판의 이름 반환
    if (boards && boards.length > 0 && boardCode) {
      const foundBoard = boards.find(board => board.code === boardCode);
      if (foundBoard) {
        title = foundBoard.name || foundBoard.code;
      }
    }
    
    return title;
  };

  // 홈으로 돌아가기 버튼
  const HomeButton = () => (
    <Button 
      variant="outlined" 
      onClick={() => navigate('/')}
      startIcon={<HomeIcon />}
      sx={{ mr: 1 }}
    >
      홈으로
    </Button>
  );
  
  // 상단 버튼 영역
  const TopButtons = () => {
    return (
      <div style={{ display: 'flex', marginBottom: '16px', alignItems: 'center' }}>
        <h2 style={{ 
          marginLeft: '16px',
          fontSize: '1.25rem',
          fontWeight: '600',
          color: '#334155',
          flexGrow: 1,
          margin: 0,
          paddingLeft: '20px'
        }}>
          {getBoardTitle()}
        </h2>
        {mode === 'list' && false && <NewPostButton />}
      </div>
    );
  };

  // renderListMode 함수: 게시글 목록 표시
  const renderListMode = () => {
    return (
      <div className="board-list-mode" style={{
        height: '100%',
        width: '100%'
      }}>
        <TopButtons />

        {postsLoading ? (
          <div style={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '24px'
          }}>
            <div style={{ 
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: '2px solid #e2e8f0',
              borderTopColor: '#3b82f6',
              animation: 'spin 0.8s linear infinite'
            }}></div>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
            <span style={{ marginLeft: '12px', fontSize: '0.875rem', color: '#64748b' }}>
              {t('loading_posts')}
            </span>
          </div>
        ) : (
          <>
            <div style={{ 
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ 
                    backgroundColor: '#f1f5f9', 
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    <th style={{ 
                      padding: '14px 12px', 
                      textAlign: 'center', 
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#334155',
                      width: '60px'
                    }}>{t('number')}</th>
                    <th style={{ 
                      padding: '14px 12px', 
                      textAlign: 'left', 
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#334155'
                    }}>{t('title')}</th>
                    <th style={{ 
                      padding: '14px 12px', 
                      textAlign: 'center', 
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#334155',
                      width: '120px'
                    }}>{t('author')}</th>
                    <th style={{ 
                      padding: '14px 12px', 
                      textAlign: 'center', 
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#334155',
                      width: '120px'
                    }}>{t('date')}</th>
                    <th style={{ 
                      padding: '14px 12px', 
                      textAlign: 'center', 
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#334155',
                      width: '70px'
                    }}>{t('views')}</th>
                  </tr>
                </thead>
                <tbody>
                  {posts && posts.length > 0 ? (
                    posts.map(post => (
                      <tr 
                        key={post.id} 
                        style={{ 
                          borderBottom: '1px solid #e2e8f0',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => navigateToDetail(post.id)}
                      >
                        <td style={{ 
                          padding: '14px 12px', 
                          textAlign: 'center',
                          fontSize: '0.875rem',
                          color: '#64748b'
                        }}>{post.id}</td>
                        <td style={{ 
                          padding: '14px 12px', 
                          textAlign: 'left',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#334155'
                        }}>
                          <div style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%'
                          }}>
                            {post.title}
                          </div>
                        </td>
                        <td style={{ 
                          padding: '14px 12px', 
                          textAlign: 'center',
                          fontSize: '0.875rem',
                          color: '#64748b'
                        }}>
                          {post.administratorId ? t('admin') : 
                           (currentUser && post.userId === currentUser.id) ? t('me') : 
                           (post.author || (post.user ? post.user.name : t('anonymous')))}
                        </td>
                        <td style={{ 
                          padding: '14px 12px', 
                          textAlign: 'center',
                          fontSize: '0.875rem',
                          color: '#64748b'
                        }}>
                          {post.date || (post.createdAt ? new Date(post.createdAt).toLocaleDateString() : '-')}
                        </td>
                        <td style={{ 
                          padding: '14px 12px', 
                          textAlign: 'center',
                          fontSize: '0.875rem',
                          color: '#64748b'
                        }}>
                          {post.views || post.viewCount || 0}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" style={{
                        padding: '32px',
                        textAlign: 'center',
                        color: '#64748b',
                        backgroundColor: '#f8fafc',
                        fontSize: '0.875rem'
                      }}>
                        {t('no_posts')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '24px',
                gap: '6px'
              }}>
                {Array.from({ length: pagination.totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => handlePageChange(i + 1)}
                    style={{
                      padding: '8px 14px',
                      border: currentPage === i + 1 ? 'none' : '1px solid #e2e8f0',
                      backgroundColor: currentPage === i + 1 ? '#3b82f6' : 'white',
                      color: currentPage === i + 1 ? 'white' : '#64748b',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: currentPage === i + 1 ? '600' : '400',
                      boxShadow: currentPage === i + 1 ? '0 2px 4px rgba(59, 130, 246, 0.3)' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // renderDetailMode 함수: 게시글 상세 표시
  const renderDetailMode = () => {
    if (postDetailLoading) {
      return (
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '24px'
        }}>
          <div style={{ 
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            border: '2px solid #e2e8f0',
            borderTopColor: '#3b82f6',
            animation: 'spin 0.8s linear infinite'
          }}></div>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
          <span style={{ marginLeft: '12px', fontSize: '0.875rem', color: '#64748b' }}>
            게시글을 불러오는 중...
          </span>
        </div>
      );
    }

    if (!postDetail) {
      return (
        <div style={{ 
          padding: '24px',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#64748b', marginBottom: '16px' }}>{t('post_not_found')}</h3>
          <button 
            onClick={navigateToList}
            style={{ 
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            {t('to_list')}
          </button>
        </div>
      );
    }

    // API 응답 구조에 따라 게시글 및 댓글 데이터 추출
    const post = postDetail.post || postDetail;
    const comments = post.children || [];

    return (
      <div className="board-detail-mode" style={{
        height: '100%',
        width: '100%'
      }}>
        <TopButtons />

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: '16px'
        }}>
          <button 
            onClick={navigateToList}
            style={{ 
              backgroundColor: '#f8fafc',
              color: '#64748b',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <ListIcon style={{ fontSize: '18px' }} />
            {t('to_list')}
          </button>

          {false && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={handleGoToEdit}
                style={{ 
                  backgroundColor: '#f8fafc',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                수정하기
              </button>
              <button 
                onClick={() => setShowDeleteModal(true)}
                style={{ 
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                삭제하기
              </button>
            </div>
          )}
        </div>

        <div style={{ 
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          marginBottom:'30px'
        }}>
          <div style={{ 
            borderBottom: '1px solid #e2e8f0',
            padding: '20px',
            backgroundColor: '#f8fafc'
          }}>
            <h2 style={{ 
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#334155',
              margin: '0 0 12px 0'
            }}>
              {post.title}
            </h2>
            <div style={{ 
              display: 'flex',
              fontSize: '0.875rem',
              color: '#64748b',
              flexWrap: 'wrap',
              gap: '12px',
              alignItems: 'center'
            }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <strong style={{ marginRight: '4px', color: '#475569' }}>{t('author')}:</strong> 
                {post.administratorId ? t('admin') : 
                 (currentUser && post.userId === currentUser.id) ? t('me') : 
                 (post.author || (post.user ? post.user.name : t('anonymous')))}
              </span>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'nowrap'
              }}>
                <strong style={{ marginRight: '4px', color: '#475569' }}>{t('date')}:</strong> {new Date(post.createdAt).toLocaleDateString()}
              </span>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center'
              }}>
                <strong style={{ marginRight: '4px', color: '#475569' }}>{t('views')}:</strong> {post.viewCount || 0}
              </span>
            </div>
          </div>
          <div style={{ 
            padding: '24px',
            minHeight: '200px',
            lineHeight: '1.6',
            fontSize: '0.9375rem',
            color: '#334155'
          }}>
            <div dangerouslySetInnerHTML={{ __html: post.content }} />  
          </div>
        </div>

        {false && (
          <div style={{ marginTop: '32px' }}>
            <h3 style={{ 
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#334155',
              marginBottom: '16px'
            }}>
              {t('comments_count', { count: comments.length })}
            </h3>
            
            <div style={{ 
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              overflow: 'hidden',
              marginBottom: '16px'
            }}>
              <div style={{ padding: '16px' }}>
                <textarea
                  value={commentData.content}
                  onChange={handleCommentChange}
                  placeholder={t('enter_comment')}
                  style={{ 
                    width: '100%',
                    minHeight: '80px',
                    padding: '12px',
                    borderRadius: '4px',
                    border: '1px solid #e2e8f0',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button 
                    onClick={handleSaveComment}
                    style={{ 
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 16px',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    {t('write_comment')}
                  </button>
                </div>
              </div>
            </div>

            {comments && comments.length > 0 ? (
              <div style={{ 
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                {comments.map((comment, index) => (
                  <div 
                    key={comment.id}
                    style={{ 
                      padding: '16px',
                      borderBottom: index < comments.length - 1 ? '1px solid #e2e8f0' : 'none'
                    }}
                  >
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}>
                      <div style={{ fontWeight: '500', fontSize: '0.875rem', color: '#334155' }}>
                        {comment.administratorId ? t('admin') : 
                        (currentUser && comment.userId === currentUser.id) ? t('me') : 
                        (comment.author || (comment.user ? comment.user.name : t('anonymous')))}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {new Date(comment.createdAt).toLocaleString()}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: '1.5' }}>
                      {comment.content}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: '16px',
                textAlign: 'center',
                color: '#64748b',
                backgroundColor: '#f8fafc',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}>
                {t('no_comments')}
              </div>
            )}
          </div>
        )}

        {showDeleteModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '400px',
              padding: '24px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}>
              <h3 style={{ 
                fontSize: '1.125rem',
                fontWeight: '600',
                color: '#334155',
                marginTop: 0,
                marginBottom: '16px'
              }}>
                {t('confirm_delete_post')}
              </h3>
              <p style={{ 
                fontSize: '0.875rem',
                color: '#4b5563',
                marginBottom: '24px'
              }}>
                {t('delete_post_warning')}
              </p>
              <div style={{ 
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px'
              }}>
                <button 
                  onClick={() => setShowDeleteModal(false)}
                  style={{ 
                    backgroundColor: '#f8fafc',
                    color: '#64748b',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {t('cancel')}
                </button>
                <button 
                  onClick={handleDeletePost}
                  style={{ 
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {t('delete')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // renderNewMode 함수: 게시글 작성 폼 표시
  const renderNewMode = () => {
    // 현재 선택된 게시판 이름 찾기
    let currentBoardName = boardTitle;
    
    // URL 경로와 target 파라미터 확인
    const urlPathSegments = location.pathname.split('/').filter(Boolean);
    const urlPath = urlPathSegments.length > 0 ? urlPathSegments[0] : '';
    
    // parameter 속성이 있으면 우선 사용
    if (categoryId) {
      // 게시판 이름 찾기
      if (boards && boards.length > 0) {
        const foundBoard = boards.find(board => board.id === categoryId);
        if (foundBoard) {
          currentBoardName = foundBoard.name || foundBoard.code;
        }
      }
    } else {
      // URL 기반 처리
      const routeTarget = urlPath || target || boardParameter;
      
      if (boards && boards.length > 0 && routeTarget) {
        const foundBoard = boards.find(board => 
          board.code === routeTarget.toUpperCase() || 
          board.code === String(routeTarget).toUpperCase()
        );
        
        if (foundBoard) {
          currentBoardName = foundBoard.name || foundBoard.code;
        }
      }
    }
    
    // 폼 UI 직접 렌더링
    return (
      <div className="board-new-mode" style={{
        height: '100%',
        width: '100%'
      }}>
        <div style={{ padding: '20px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px',
            borderBottom: '2px solid #f1f5f9',
            paddingBottom: '10px'
          }}>
            <h2 style={{ 
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#334155',
              margin: 0
            }}>
              {currentBoardName} - {t('write_new_post')}
            </h2>
            <button 
              onClick={navigateToList}
              style={{ 
                backgroundColor: '#f8fafc',
                color: '#64748b',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                padding: '8px 16px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              {t('to_list')}
            </button>
          </div>

          <div style={{ 
            borderRadius: '6px',
            overflow: 'hidden',
            padding: '16px',
            backgroundColor: '#f8fafc'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <label 
                htmlFor="title"
                style={{ 
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#4b5563'
                }}
              >
                {t('title')}
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder={t('enter_title')}
                style={{ 
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '4px',
                  border: '1px solid #e2e8f0',
                  fontSize: '1rem',
                  backgroundColor: '#fff'
                }}
              />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label 
                htmlFor="content"
                style={{ 
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#4b5563'
                }}
              >
                {t('content')}
              </label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder={t('enter_content')}
                style={{ 
                  width: '100%',
                  minHeight: '300px',
                  padding: '16px',
                  borderRadius: '4px',
                  border: '1px solid #e2e8f0',
                  fontSize: '1rem',
                  resize: 'vertical',
                  lineHeight: '1.5',
                  backgroundColor: '#fff'
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
              <button 
                onClick={handleSavePost}
                style={{ 
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '12px 24px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  width: '50%',
                  maxWidth: '200px'
                }}
              >
                {t('register_post')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // renderEditMode 함수: 게시글 수정 폼 표시
  const renderEditMode = () => {
    return (
      <div className="board-edit-mode" style={{
        height: '100%',
        width: '100%'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: '16px'
        }}>
          <h3 style={{ 
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#334155',
            margin: 0
          }}>
            {t('edit_post')}
          </h3>
          <button 
            onClick={() => setMode('detail')}
            style={{ 
              backgroundColor: '#f8fafc',
              color: '#64748b',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            {t('cancel')}
          </button>
        </div>

        <div style={{ 
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          overflow: 'hidden',
          padding: '16px'
        }}>
          <div style={{ marginBottom: '16px' }}>
            <label 
              htmlFor="edit-title"
              style={{ 
                display: 'block',
                marginBottom: '8px',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#4b5563'
              }}
            >
              {t('title')}
            </label>
            <input
              type="text"
              id="edit-title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder={t('enter_title')}
              style={{ 
                width: '100%',
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #e2e8f0',
                fontSize: '0.875rem'
              }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label 
              htmlFor="edit-content"
              style={{ 
                display: 'block',
                marginBottom: '8px',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#4b5563'
              }}
            >
              {t('content')}
            </label>
            <textarea
              id="edit-content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder={t('enter_content')}
              style={{ 
                width: '100%',
                minHeight: '300px',
                padding: '12px',
                borderRadius: '4px',
                border: '1px solid #e2e8f0',
                fontSize: '0.875rem',
                resize: 'vertical',
                lineHeight: '1.5'
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button 
              onClick={handleSavePost}
              style={{ 
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              {t('edit_complete')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 모드에 따른 콘텐츠 렌더링
  const renderContent = () => {
    // 간단히 모드에 따라 렌더링
    switch (mode) {
      case 'list':
         return renderListMode();
      // case 'detail': return renderDetailMode();
      case 'new':
         return renderNewMode();
      case 'edit':
         return renderEditMode();
      default:
         return renderDetailMode();
    }
  };

  

  // 여백 설정 초기화 및 변경 감지를 위한 효과
  useEffect(() => {
    if (style && style.device) {
      // 현재 디바이스별 패딩 값 로그 출력 (디버깅용)
      const devicePaddings = {
        pc: {
          paddingTop: style.device?.pc?.paddingTop || '0px',
          paddingRight: style.device?.pc?.paddingRight || '0px',
          paddingBottom: style.device?.pc?.paddingBottom || '0px',
          paddingLeft: style.device?.pc?.paddingLeft || '0px'
        },
        mobile: {
          paddingTop: style.device?.mobile?.paddingTop || '0px',
          paddingRight: style.device?.mobile?.paddingRight || '0px',
          paddingBottom: style.device?.mobile?.paddingBottom || '0px',
          paddingLeft: style.device?.mobile?.paddingLeft || '0px'
        }
      };
      
      
      // 강제 업데이트를 통해 스타일 재계산
      setForceUpdate(prev => prev + 1);
    }
  }, [style]);

  // 배포 환경에서 여백 설정을 강제 적용하기 위한 효과
  useEffect(() => {
    // 컴포넌트 마운트 후 일정 시간 뒤에 강제로 여백 스타일 적용
    const applyStyles = () => {
      try {
        const boardEl = document.querySelector('.board-component-container');
        if (boardEl) {
          const paddingTop = getSimpleStyle('paddingTop', '0px');
          const paddingRight = getSimpleStyle('paddingRight', '0px');
          const paddingBottom = getSimpleStyle('paddingBottom', '0px');
          const paddingLeft = getSimpleStyle('paddingLeft', '0px');
          
          // DOM API를 사용하여 최대 우선순위로 스타일 강제 적용
          boardEl.style.setProperty('padding-top', paddingTop, 'important');
          boardEl.style.setProperty('padding-right', paddingRight, 'important');
          boardEl.style.setProperty('padding-bottom', paddingBottom, 'important');
          boardEl.style.setProperty('padding-left', paddingLeft, 'important');
          
          // 스타일시트를 직접 수정하는 방식 추가
          let styleSheet = document.getElementById('board-component-styles');
          if (!styleSheet) {
            styleSheet = document.createElement('style');
            styleSheet.id = 'board-component-styles';
            document.head.appendChild(styleSheet);
          }
          
          const cssRules = `
            .board-component-container {
              padding-top: ${paddingTop} !important;
              padding-right: ${paddingRight} !important;
              padding-bottom: ${paddingBottom} !important;
              padding-left: ${paddingLeft} !important;
              box-sizing: border-box !important;
            }
          `;
          
          styleSheet.innerHTML = cssRules;
          
          
        }
      } catch (error) {
        console.error('[BoardComponent] 여백 스타일 적용 오류:', error);
      }
    };

    // 여러 번 호출하여 배포 환경에서도 확실히 적용되도록 함
    const timer1 = setTimeout(applyStyles, 0);
    const timer2 = setTimeout(applyStyles, 100);
    const timer3 = setTimeout(applyStyles, 500);
    const timer4 = setTimeout(applyStyles, 1000);
    
    // 브라우저 window load 이벤트 발생 시 한 번 더 적용
    const handleWindowLoad = () => {
      setTimeout(applyStyles, 100);
      setTimeout(applyStyles, 500);
    };
    window.addEventListener('load', handleWindowLoad);
    
    // 스크롤 이벤트 발생 시에도 적용
    const handleScroll = () => {
      applyStyles();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      window.removeEventListener('load', handleWindowLoad);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [style, currentDevice]);

  // 스타일 강제 업데이트 감지 및 처리
  useEffect(() => {
    // __forceUpdatePadding 속성이 있으면 강제로 업데이트
    if (style && style.__forceUpdatePadding) {
     
      
      // 즉시 스타일 적용
      const boardElement = document.querySelector('.board-component-container');
      if (boardElement) {
        boardElement.style.setProperty('padding-top', getSimpleStyle('paddingTop', '0px'), 'important');
        boardElement.style.setProperty('padding-right', getSimpleStyle('paddingRight', '0px'), 'important');
        boardElement.style.setProperty('padding-bottom', getSimpleStyle('paddingBottom', '0px'), 'important');
        boardElement.style.setProperty('padding-left', getSimpleStyle('paddingLeft', '0px'), 'important');
      }
      
      // 순차적으로 여러 번 업데이트 시도
      setForceUpdate(prev => prev + 1);
      setTimeout(() => setForceUpdate(prev => prev + 1), 100);
      setTimeout(() => setForceUpdate(prev => prev + 1), 500);
    }
  }, [style]);


  return (
    <section 
      className="board-component-container"
      data-device={currentDevice}
      data-update-count={forceUpdate}
      style={{ 
        position: 'relative',
        paddingTop: style?.paddingTop || '0px',
        paddingRight: style?.paddingRight || '0px',
        paddingBottom: style?.paddingBottom || '0px',
        paddingLeft: style?.paddingLeft || '0px',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        overflow: 'auto',
        backgroundColor: style?.backgroundColor || 'transparent',
        borderRadius: style?.borderRadius || '0px',
        boxShadow: style?.boxShadow || 'none',
        fontFamily: 'Inter, sans-serif'
      }}
    >
      {renderContent()}
    </section>
  );
}

export default BoardComponent; 